/**
 * Shared pieces of area (a) — creation back-office: navigation context (so (f)'s AG-Demandes / AG-Fiche / AG-Lecteur
 * can mount the same list and drawer under their own paths), labels, chips and small layout blocks.
 */
import { createContext, useContext, type ComponentProps, type ReactNode } from "react";
import { useNavigate } from "@tanstack/react-router";
import type { BoDossier, BoTab, WaitingOn } from "@/contracts/areas/creation";
import { AE_PHASES, ENDED_STATES } from "@/contracts/shared/creation-tracking.ts";
import { COMPANY_PHASES } from "@/contracts/shared/creation.ts";
import { cn } from "@/lib/cn";

// ───────────────────────── Navigation ─────────────────────────

export type Fenetre =
  | "invalider" | "invalider-piece" | "envoyer" | "revue" | "reinvalider" | "revue-capital" | "apercu-piece" | "apercu-document"
  | "lien-jal" | "attestation" | "lien-greffe" | "liasse" | "demande-inpi" | "refus-inpi" | "immatriculation" | "annuler" | "vue-client"
  | "rejeter" | "rouvrir" | "attribuer" | "transition" | "paiement" | "revue-finale" | "document-officiel" | "transmettre-inpi";

export const FENETRES: readonly Fenetre[] = [
  "invalider", "invalider-piece", "envoyer", "revue", "reinvalider", "revue-capital", "apercu-piece", "apercu-document",
  "lien-jal", "attestation", "lien-greffe", "liasse", "demande-inpi", "refus-inpi", "immatriculation", "annuler", "vue-client",
  "rejeter", "rouvrir", "attribuer", "transition", "paiement", "revue-finale", "document-officiel", "transmettre-inpi",
];

/** Search params of the dossier route: which window is open, on which field / piece / document / review point. */
export interface DossierSearch {
  fenetre?: Fenetre;
  champ?: string;
  piece?: string;
  doc?: string;
  point?: string;
  kind?: string;
  /** List tab drawn behind the fiche (the tab the fiche was opened from); falls back to the dossier's own tab. */
  onglet?: BoTab;
  /** Form step(s) of « Formulaire du demandeur » to open, « 3 » or « 3,5 » (also `#etape-3`). */
  etape?: string;
  /** Pre-filled amount of a payment-link window, « 189,00 ». */
  montant?: string;
  /** Pre-filled reason of « Annuler la demande ». */
  motif?: string;
}

export interface CreationNav {
  /** List URL of a tab (with the type filter of BO-Creation-Attente-AE). */
  listHref: (tab: BoTab, type?: "ae" | "company") => string;
  dossierHref: (id: string, search?: DossierSearch) => string;
}

const qs = (o: Record<string, string | undefined>) => {
  const p = new URLSearchParams();
  for (const [k, v] of Object.entries(o)) if (v) p.set(k, v);
  const s = p.toString();
  return s ? `?${s}` : "";
};

export const TAB_PATH: Record<BoTab, string> = {
  nouvelles: "/creation/nouvelles",
  "en-attente": "/creation/en-attente",
  "attente-client": "/creation/attente-client",
  terminees: "/creation/terminees",
};

export const defaultCreationNav: CreationNav = {
  listHref: (tab, type) => `${TAB_PATH[tab]}${qs({ type: type === "ae" ? "auto-entrepreneur" : type === "company" ? "societes" : undefined })}`,
  dossierHref: (id, s = {}) => `/creation/dossiers/${id}${qs({ ...s })}`,
};

const NavCtx = createContext<CreationNav>(defaultCreationNav);
export const CreationNavProvider = NavCtx.Provider;
export const useCreationNav = () => useContext(NavCtx);

/** Plain link to an app href (works under any route tree): client-side navigation, real <a> for middle-click. */
export function HrefLink({ href, replace, onClick, ...rest }: ComponentProps<"a"> & { href: string; replace?: boolean }) {
  const navigate = useNavigate();
  return (
    <a
      href={href}
      onClick={(e) => {
        onClick?.(e);
        if (e.defaultPrevented || e.metaKey || e.ctrlKey || e.shiftKey || e.button !== 0) return;
        e.preventDefault();
        void navigate({ href, replace });
      }}
      {...rest}
    />
  );
}

export function useGo() {
  const navigate = useNavigate();
  return (href: string, replace = false) => void navigate({ href, replace });
}

// ───────────────────────── Labels ─────────────────────────

/** Short stepper labels of the fiche (« Décision », not « Décision et espace après création »). */
export const STEPPER_COMPANY = ["Préparation", "Vérification Melrys", "Dépôt du capital", "Finalisation des documents", "Dépôt de l'annonce légale", "Dépôt INPI", "Vérification INPI", "Décision"];
export const STEPPER_AE = ["Préparation", "Vérification Melrys", "Pièces et signature", "Dépôt au Guichet unique", "Validation du Guichet unique", "Immatriculation"];

export const COMPANY_PHASE_LABELS = COMPANY_PHASES;
export const AE_PHASE_LABELS = AE_PHASES;

/** AE phase → the company phase it sits under in the « Tous types » pipeline (no capital, no legal notice). */
export const AE_TO_COMPANY_PHASE = [0, 1, 2, 4, 6, 7, 8] as const;

export const isEnded = (d: Pick<BoDossier, "tracking">) => ENDED_STATES.includes(d.tracking.state);

export const tabOfDossier = (d: BoDossier): BoTab =>
  isEnded(d) ? "terminees" : !d.agent && d.tracking.state === "PRECHECK_SUBMITTED" ? "nouvelles" : d.waitingOn === "client" ? "attente-client" : "en-attente";

export const formLabel = (form: string) => (form === "AE" ? "Auto-entrepreneur" : form);

const pad = (n: number) => String(n).padStart(2, "0");
/** « 05/09/26 » */
export function shortDate(iso: string) {
  const d = new Date(iso);
  return `${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${String(d.getFullYear()).slice(2)}`;
}
/** « 05/09/2026 » (date-only strings are not shifted by the time zone). */
export function frDate(iso: string) {
  if (/^\d{4}-\d{2}-\d{2}$/.test(iso)) return iso.split("-").reverse().join("/");
  const d = new Date(iso);
  return `${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${d.getFullYear()}`;
}
export function frDateTime(iso: string) {
  const d = new Date(iso);
  return `${frDate(iso)} à ${pad(d.getHours())}:${pad(d.getMinutes())}`;
}
/** « il y a 16 j » / « il y a 1 mois » */
export function ago(iso: string, now = Date.now()) {
  const days = Math.max(0, Math.floor((now - new Date(iso).getTime()) / 86_400_000));
  if (days === 0) return "aujourd'hui";
  if (days < 30) return `il y a ${days} j`;
  const months = Math.floor(days / 30);
  return `il y a ${months} mois`;
}
export const isoToday = () => {
  const d = new Date();
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};
export const inDays = (n: number) => {
  const d = new Date(Date.now() + n * 86_400_000);
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};

export function initialsOf(name: string) {
  return name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0]!.toUpperCase())
    .join("");
}

export const REASONS = [
  { id: "unreadable", label: "Document illisible" },
  { id: "mismatch", label: "Ne correspond pas à la demande" },
  { id: "wrong_info", label: "Information erronée" },
  { id: "incomplete", label: "Champ incomplet" },
  { id: "other", label: "Autre motif" },
] as const;
export const REASON_LABEL: Record<string, string> = {
  unreadable: "Document illisible",
  mismatch: "Ne correspond pas à la demande",
  wrong_info: "Information erronée",
  incomplete: "Champ incomplet",
  missing_document: "Document manquant",
  other: "Autre motif",
};

export function eurCents(cents: number) {
  return new Intl.NumberFormat("fr-FR", { style: "currency", currency: "EUR" }).format(cents / 100).replace(/\u202f/g, "\u00a0");
}

// ───────────────────────── Chips & blocks ─────────────────────────

const CHIP: Record<string, string> = {
  amber: "bg-[#FEF3E0] text-[#92400E]",
  grey: "bg-[#F1F2F6] text-[#666A7B]",
  green: "bg-[#E7F6EC] text-[#15803D]",
  red: "bg-[#FDEAEA] text-[#B42318]",
  indigo: "bg-[#EEF0FE] text-[#4B45CC]",
  dark: "bg-ink text-white",
};
export type ChipTone = keyof typeof CHIP;

export function Chip({ tone = "grey", className, children, title }: { tone?: ChipTone; className?: string; children: ReactNode; title?: string }) {
  return (
    <span title={title} className={cn("inline-flex max-w-full items-center gap-[5px] rounded-full px-2.5 py-[3px] text-[11px] font-extrabold whitespace-nowrap", CHIP[tone], className)}>
      {children}
    </span>
  );
}

export const WAITING_LABEL: Record<WaitingOn, string> = { melrys: "Melrys", client: "Client", inpi: "INPI", done: "Clos" };
export function WaitingChip({ on }: { on: WaitingOn }) {
  return <Chip tone={on === "melrys" ? "amber" : "grey"}>{WAITING_LABEL[on]}</Chip>;
}

/** White section of the fiche (radius 20, padding 20/22). */
export function Sec({ id, title, aside, subtitle, className, children }: { id?: string; title: ReactNode; aside?: ReactNode; subtitle?: ReactNode; className?: string; children?: ReactNode }) {
  return (
    <section id={id} className={cn("scroll-mt-4 rounded-[20px] border border-line bg-white px-4 py-5 sm:px-[22px]", className)}>
      <div className="flex flex-wrap items-start justify-between gap-x-3 gap-y-1.5">
        <h3 className="min-w-0 text-[15.5px] font-extrabold text-ink">{title}</h3>
        {aside}
      </div>
      {subtitle && <p className="mt-1 text-[12.5px] leading-relaxed text-muted">{subtitle}</p>}
      {children}
    </section>
  );
}

export function Avatar36({ name, className }: { name: string; className?: string }) {
  return (
    <span aria-hidden className={cn("inline-flex size-9 shrink-0 items-center justify-center rounded-full bg-[linear-gradient(135deg,#6D5CF0,#3B82F6)] text-xs font-extrabold text-white", className)}>
      {initialsOf(name)}
    </span>
  );
}

/** Mini progress bar of a row (one pill per phase: done green, current indigo). */
export function PhaseBar({ phase, count }: { phase: number; count: number }) {
  return (
    <span className="mt-0.5 flex gap-[3px]" aria-hidden>
      {Array.from({ length: count }, (_, i) => (
        <i key={i} className={cn("h-[5px] w-3.5 rounded-full", i + 1 < phase ? "bg-[#15803D]" : i + 1 === phase ? "bg-primary-500" : "bg-[#E4E7EE]")} />
      ))}
    </span>
  );
}

/** Back-office label of each state (Flows-Etats, « Transition manuelle d'état »). */
export const STATE_LABEL: Record<string, string> = {
  AWAITING_CREATION_PAYMENT: "Paiement de la création",
  PRECHECK_SUBMITTED: "À prévalider",
  MELRYS_INFO_REQUESTED: "Informations demandées au client",
  MELRYS_INFO_RECEIVED: "Réponse du client à vérifier",
  PRECHECK_APPROVED: "Prévalidé",
  WAITING_FOR_CAPITAL_DEPOSIT: "En attente du dépôt du capital",
  CAPITAL_CERTIFICATE_REVIEW: "Attestation à vérifier",
  FINALIZATION_IN_PROGRESS: "Finalisation en cours",
  READY_FOR_FINAL_SUBMISSION: "Prêt à transmettre (client)",
  FINAL_SUBMITTED: "Dossier final à contrôler",
  LEGAL_NOTICE_PREPARATION: "Annonce légale à préparer",
  AWAITING_JAL_PAYMENT: "Paiement de l'annonce en attente",
  READY_TO_FILE: "Prêt à déposer",
  FILED_WITH_INPI: "Déposé à l'INPI",
  INPI_INFO_REQUESTED: "Demande INPI en cours",
  INPI_INFO_RECEIVED: "Réponse INPI à transmettre",
  INPI_RESPONSE_SUBMITTED: "Réponse transmise à l'INPI",
  COMPANY_CREATED: "Société immatriculée",
  REJECTED_BY_MELRYS: "Rejeté par Melrys",
  REJECTED_BY_INPI: "Rejeté par l'INPI",
  CANCELLED: "Demande annulée",
  CLOSED_INACTIVE: "Fermé pour inactivité",
};
